import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { clearApiCaches, fetchAnalyticsDetail, fetchData, fetchFullAnalyticsHistory, getCachedAnalyticsData, isFullAnalyticsHistoryLoaded } from '../api';
import { Analytics } from '../types';
import { useAuth } from './AuthContext';
import { getCorporateCatalystsMapAsync } from '../hooks/useStockCatalysts';
import { clearMtfCache } from '../services/mtfService';

interface AnalyticsDataContextValue {
  data: Analytics[];
  selectedDate: string;
  setSelectedDate: React.Dispatch<React.SetStateAction<string>>;
  selectedData: Analytics | null;
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  refreshData: () => Promise<void>;
  ensureFullHistoryLoaded: () => Promise<Analytics[]>;
  isFullHistoryLoaded: boolean;
  loadAnalyticsDetail?: (date?: string, forceRefresh?: boolean, includeSectorSignals?: boolean) => Promise<Analytics | null>;
}

export const AnalyticsDataContext = createContext<AnalyticsDataContextValue | undefined>(undefined);

const SELECTED_DATE_STORAGE_KEY = 'niftysutra.selected-analytics-date';

// Clear any stale persisted date from previous versions to guarantee latest data defaults
if (typeof window !== 'undefined') {
  try {
    window.localStorage.removeItem(SELECTED_DATE_STORAGE_KEY);
  } catch {
    // Ignore storage errors in restricted contexts
  }
}

const sortAnalyticsData = (analyticsData: Analytics[]) =>
  [...analyticsData].sort((a, b) => new Date(b.Date).getTime() - new Date(a.Date).getTime());

export const AnalyticsDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAdmin, isAuthenticated, loading: authLoading } = useAuth();

  // Initialize synchronously with cached data if available so latest session is ready immediately
  const [data, setData] = useState<Analytics[]>(() => {
    const cached = getCachedAnalyticsData();
    return cached && cached.length > 0 ? sortAnalyticsData(cached) : [];
  });
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const cached = getCachedAnalyticsData();
    if (cached && cached.length > 0) {
      const sorted = sortAnalyticsData(cached);
      return sorted[0]?.Date ?? '';
    }
    return '';
  });
  const [selectedData, setSelectedData] = useState<Analytics | null>(() => {
    const cached = getCachedAnalyticsData();
    if (cached && cached.length > 0) {
      const sorted = sortAnalyticsData(cached);
      return sorted[0] ?? null;
    }
    return null;
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isFullHistoryLoaded, setIsFullHistoryLoaded] = useState<boolean>(() => isFullAnalyticsHistoryLoaded());

  const dataRef = useRef<Analytics[]>(data);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const isFullHistoryLoadedRef = useRef<boolean>(isFullHistoryLoaded);
  useEffect(() => {
    isFullHistoryLoadedRef.current = isFullHistoryLoaded;
  }, [isFullHistoryLoaded]);

  const selectedDateRef = useRef(selectedDate);
  useEffect(() => {
    selectedDateRef.current = selectedDate;
  }, [selectedDate]);

  const ensureFullHistoryLoaded = useCallback(async (): Promise<Analytics[]> => {
    if (isFullHistoryLoadedRef.current && dataRef.current.length > 300) {
      return dataRef.current;
    }

    try {
      const fullHistory = await fetchFullAnalyticsHistory();
      const byDate = new Map<string, Analytics>();
      for (const item of fullHistory) {
        if (item?.Date) byDate.set(item.Date, item);
      }
      // Preserve any fresher records already loaded in dataRef.current (e.g., today's live calculator run)
      for (const item of dataRef.current) {
        if (item?.Date) byDate.set(item.Date, item);
      }
      const sorted = sortAnalyticsData(Array.from(byDate.values()));
      const prevLatestDate = dataRef.current.length > 0 ? dataRef.current[0].Date : '';
      dataRef.current = sorted;
      isFullHistoryLoadedRef.current = true;
      setData(sorted);
      setIsFullHistoryLoaded(true);

      if (sorted.length > 0) {
        const latestDate = sorted[0].Date;
        const currentSelected = selectedDateRef.current;
        if (!currentSelected || currentSelected === prevLatestDate) {
          selectedDateRef.current = latestDate;
          setSelectedDate(latestDate);
        }
      }

      return sorted;
    } catch (err) {
      console.error('Failed to load full analytics history:', err);
      return dataRef.current;
    }
  }, []);

  const loadAnalyticsDetail = useCallback(
    async (dateToLoad?: string, forceRefresh = false, includeSectorSignals = false): Promise<Analytics | null> => {
      const targetDate = dateToLoad || selectedDateRef.current;
      if (!targetDate) {
        return null;
      }

      const summaryMatch = dataRef.current.find((item) => item.Date === targetDate) ?? null;

      // When user is not logged in, we should not even make a call to /api/data/analytics/:date api
      if (!isAuthenticated) {
        return summaryMatch;
      }

      try {
        const detail = await fetchAnalyticsDetail(targetDate, { forceRefresh, includeSectorSignals });
        const latestSummaryMatch = dataRef.current.find((item) => item.Date === targetDate) ?? summaryMatch;
        if (detail) {
          const merged: Analytics = latestSummaryMatch
            ? {
                ...latestSummaryMatch,
                ...detail,
                BreakoutStocks:
                  detail.BreakoutStocks && detail.BreakoutStocks.length > 0
                    ? detail.BreakoutStocks
                    : (latestSummaryMatch.BreakoutStocks ?? []),
                BreakoutStockCount: detail.BreakoutStockCount || latestSummaryMatch.BreakoutStockCount || 0,
                FourPerPositiveClose:
                  detail.FourPerPositiveClose && detail.FourPerPositiveClose > 0
                    ? detail.FourPerPositiveClose
                    : (latestSummaryMatch.FourPerPositiveClose ?? 0),
                FourPerNegativeClose:
                  detail.FourPerNegativeClose && detail.FourPerNegativeClose > 0
                    ? detail.FourPerNegativeClose
                    : (latestSummaryMatch.FourPerNegativeClose ?? 0),
                PositiveClose:
                  detail.PositiveClose && detail.PositiveClose > 0
                    ? detail.PositiveClose
                    : (latestSummaryMatch.PositiveClose ?? 0),
                NegativeClose:
                  detail.NegativeClose && detail.NegativeClose > 0
                    ? detail.NegativeClose
                    : (latestSummaryMatch.NegativeClose ?? 0),
                lstAbvAllEma:
                  detail.lstAbvAllEma && detail.lstAbvAllEma.length > 0
                    ? detail.lstAbvAllEma
                    : (latestSummaryMatch.lstAbvAllEma ?? []),
                lstAbv21Ema:
                  detail.lstAbv21Ema && detail.lstAbv21Ema.length > 0
                    ? detail.lstAbv21Ema
                    : (latestSummaryMatch.lstAbv21Ema ?? []),
                Ema21_50_200_Stocks:
                  detail.Ema21_50_200_Stocks && detail.Ema21_50_200_Stocks.length > 0
                    ? detail.Ema21_50_200_Stocks
                    : (latestSummaryMatch.Ema21_50_200_Stocks ?? []),
                Ema21_50_200_Count:
                  detail.Ema21_50_200_Count ||
                  latestSummaryMatch.Ema21_50_200_Count ||
                  (detail.Ema21_50_200_Stocks?.length ?? latestSummaryMatch.Ema21_50_200_Stocks?.length ?? 0),
                VolumeShockers:
                  detail.VolumeShockers && detail.VolumeShockers.length > 0
                    ? detail.VolumeShockers
                    : (latestSummaryMatch.VolumeShockers ?? []),
                VolumeShockerCount: detail.VolumeShockerCount || latestSummaryMatch.VolumeShockerCount || 0,
                OverSold:
                  detail.OverSold && detail.OverSold.length > 0
                    ? detail.OverSold
                    : (latestSummaryMatch.OverSold ?? []),
                OverSoldCount: detail.OverSoldCount || latestSummaryMatch.OverSoldCount || 0,
                Overbought:
                  detail.Overbought && detail.Overbought.length > 0
                    ? detail.Overbought
                    : (latestSummaryMatch.Overbought ?? []),
                OverboughtCount: detail.OverboughtCount || latestSummaryMatch.OverboughtCount || 0,
              }
            : detail;

          if (targetDate === selectedDateRef.current) {
            setSelectedData(merged);
          }
          return merged;
        }
        return latestSummaryMatch;
      } catch (err) {
        console.error('Error loading analytics detail on-demand:', err);
        return summaryMatch;
      }
    },
    [isAuthenticated]
  );

  const loadAnalyticsData = useCallback(async (isUserInitiatedRefresh = false) => {
    const currentData = dataRef.current;
    if (isUserInitiatedRefresh && currentData.length > 0) {
      setRefreshing(true);
      clearMtfCache();
      clearApiCaches();
    } else {
      setLoading(true);
    }

    setError(null);

    try {
      const jsonData = await fetchData({ forceRefresh: isUserInitiatedRefresh });
      const sortedData = sortAnalyticsData(jsonData);
      dataRef.current = sortedData;
      setData(sortedData);

      if (sortedData.length > 0) {
        const latestDate = sortedData[0].Date;
        const currentSelected = selectedDateRef.current;
        const prevLatestDate = currentData.length > 0 ? currentData[0].Date : '';

        // If latest data is available, default to the latest date:
        // 1) On initial load or if no date is selected yet -> select latestDate
        // 2) If the user was viewing the previous latest session -> automatically advance to latestDate
        // 3) If the selected date does not exist in the new dataset -> default to latestDate
        // 4) If a user has explicitly navigated to an older historical session during the active session -> preserve it
        const wasOnLatest = !currentSelected || currentSelected === prevLatestDate;
        const dateExistsInNewData = sortedData.some((item) => item.Date === currentSelected);

        const nextDate = wasOnLatest || !dateExistsInNewData ? latestDate : currentSelected;

        selectedDateRef.current = nextDate;
        setSelectedDate(nextDate);
        const match = sortedData.find((item) => item.Date === nextDate) ?? null;
        setSelectedData((prev) => (prev?.Date === nextDate ? { ...match, ...prev } : match));
      } else {
        selectedDateRef.current = '';
        setSelectedDate('');
        setSelectedData(null);
      }
    } catch (err) {
      // Offline fallback: fall back to cached data if network failed
      const cached = getCachedAnalyticsData();
      if (cached && cached.length > 0) {
        const sortedCached = sortAnalyticsData(cached);
        dataRef.current = sortedCached;
        setData(sortedCached);
        const latestCachedDate = sortedCached[0].Date;
        const currentSelected = selectedDateRef.current;
        const nextDate =
          !currentSelected || !sortedCached.some((item) => item.Date === currentSelected)
            ? latestCachedDate
            : currentSelected;
        selectedDateRef.current = nextDate;
        setSelectedDate(nextDate);
        setSelectedData(sortedCached.find((item) => item.Date === nextDate) ?? sortedCached[0]);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
      }
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Full analytics history is lazy-loaded on-demand when an older date is selected (see lines below)
  // or when explicitly requested by historical components, preventing eager 1.5MB archive download on initial page load.

  // Proactively preload corporate catalysts summary for admin users so chart catalyst icons are instant
  useEffect(() => {
    if (!authLoading && isAdmin) {
      void getCorporateCatalystsMapAsync();
    }
  }, [authLoading, isAdmin]);

  const handleSetSelectedDate: React.Dispatch<React.SetStateAction<string>> = useCallback(
    (action) => {
      setSelectedDate((prev) => {
        return typeof action === 'function' ? action(prev) : action;
      });
    },
    []
  );

  const hasLoadedInitialRef = useRef(false);

  useEffect(() => {
    if (hasLoadedInitialRef.current) {
      return undefined;
    }
    hasLoadedInitialRef.current = true;
    void loadAnalyticsData(false);
    return undefined;
  }, [loadAnalyticsData]);

  useEffect(() => {
    if (!selectedDate) {
      setSelectedData(null);
      return;
    }

    const summaryMatch = data.find((item) => item.Date === selectedDate) ?? null;
    setSelectedData((prev) => {
      if (prev?.Date === selectedDate) {
        return summaryMatch ? { ...summaryMatch, ...prev } : prev;
      }
      return summaryMatch;
    });
  }, [data, selectedDate]);

  useEffect(() => {
    if (isAuthenticated && selectedDate) {
      void loadAnalyticsDetail(selectedDate);
    }
  }, [selectedDate, isAuthenticated, loadAnalyticsDetail]);

  useEffect(() => {
    if (selectedDate && data.length > 0) {
      const oldestDate = data[data.length - 1]?.Date;
      if (oldestDate && selectedDate < oldestDate && !isFullHistoryLoaded) {
        void ensureFullHistoryLoaded();
      }
    }
  }, [selectedDate, data, isFullHistoryLoaded, ensureFullHistoryLoaded]);

  const value = useMemo(
    () => ({
      data,
      selectedDate,
      setSelectedDate: handleSetSelectedDate,
      selectedData,
      loading,
      refreshing,
      error,
      refreshData: async () => loadAnalyticsData(true),
      ensureFullHistoryLoaded,
      isFullHistoryLoaded,
      loadAnalyticsDetail,
    }),
    [data, error, handleSetSelectedDate, loading, loadAnalyticsData, refreshing, selectedData, selectedDate, ensureFullHistoryLoaded, isFullHistoryLoaded, loadAnalyticsDetail]
  );

  return <AnalyticsDataContext.Provider value={value}>{children}</AnalyticsDataContext.Provider>;
};

export const useAnalyticsDataContext = () => {
  const context = useContext(AnalyticsDataContext);

  if (!context) {
    throw new Error('useAnalyticsData must be used within an AnalyticsDataProvider');
  }

  return context;
};